import { useEffect, useRef } from "react";

interface Props {
  open: boolean;
  onClose: () => void;
}

const STAT_ROWS = [
  { label: "Live data sources", value: "35+" },
  { label: "Map layer presets", value: "10 lenses" },
  { label: "Campus buildings (3D)", value: "201" },
  { label: "Road segments", value: "2,964" },
  { label: "Campus zones + dormitory belt", value: "covered" },
  { label: "Campus gates", value: "26" },
  { label: "AI model (primary)", value: "Gemma 4 · local" },
  { label: "Vector RAG index", value: "nomic-embed-text" },
];

const HIGHLIGHTS = [
  { label: "REAL DATA ONLY", desc: "BMA GIS, AQICN, iTIC Longdo, Open-Meteo, Traffy Fondue, GISTDA, AlphaEarth, Yahoo Finance — no mockups, ever." },
  { label: "SOVEREIGN AI STACK", desc: "Gemma 4 (9.6 GB, 128 K context) runs locally on-campus hardware 24/7. RAG over a live news archive using nomic-embed-text embeddings. No cloud quota, no data leaves the building." },
  { label: "X-RAY VIEW (3DS)", desc: "Top-bar 2D→3D→3DS cycles into underground mode: buildings ghost, MEA/MWA corridors and Lat Krabang khlong drains drop to burial depth (−2 / −3 / −4 m), SRT/ARL corridor anchors the stack. Reference corridors until as-built CAD is licensed." },
  { label: "TRANSIT GATEWAY", desc: "Campus sits beside the Airport Rail Link Lat Krabang station, the SRT Eastern line and Motorway No.7, minutes from Suvarnabhumi Airport — the connectivity context for the eastern Bangkok corridor, visualised on the VIB lens." },
];

export function AboutModal({ open, onClose }: Props) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    dialogRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="about-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="About KMITL Control Tower"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="about-panel" ref={dialogRef} tabIndex={-1}>
        <header className="about-head">
          <div>
            <span className="eyebrow mono" style={{ color: "var(--accent)" }}>
              KMITL CONTROL TOWER
            </span>
            <h2 className="about-title">Campus Operations Intelligence</h2>
            <p className="about-sub mono">
              King Mongkut's Institute of Technology Ladkrabang · Lat Krabang, Bangkok · founded 1960
            </p>
          </div>
          <button className="about-close mono" onClick={onClose} aria-label="Close">
            [ESC] CLOSE
          </button>
        </header>

        <div className="about-body">
          <div className="about-highlights">
            {HIGHLIGHTS.map(({ label, desc }) => (
              <div key={label} className="about-highlight">
                <span className="about-hl-label mono">{label}</span>
                <p className="about-hl-desc">{desc}</p>
              </div>
            ))}
          </div>

          <aside className="about-stats">
            <div className="about-stats-head mono">BY THE NUMBERS</div>
            {STAT_ROWS.map(({ label, value }) => (
              <div key={label} className="about-stat-row">
                <span className="about-stat-label">{label}</span>
                <span className="about-stat-value mono">{value}</span>
              </div>
            ))}
          </aside>
        </div>

        <footer className="about-foot mono">
          <span style={{ color: "var(--ink-low)" }}>
            Developed under the Dr Non Smart City Thailand portfolio · co-sponsored by depa · open-data mandate
          </span>
          <div className="about-foot-actions">
            <a
              href="/about.html"
              className="about-foot-link mono"
              aria-label="Open the full architecture page in a new view — data sources, tech stack, replicate guide, glossary"
              title="Full architecture page — data sources, tech stack, replicate guide, glossary"
            >
              ⓘ FULL ARCHITECTURE →
            </a>
            <button className="about-begin mono" onClick={onClose}>
              EXPLORE DASHBOARD →
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
